// Encryption utilities using Web Crypto API
// Same encryption as extension for data compatibility

let encryptionKey = null;

/**
 * Derive an encryption key from user ID
 * @param {string} userId - Firebase user ID
 * @returns {Promise<CryptoKey>} Derived encryption key
 */
export async function deriveKey(userId) {
    const encoder = new TextEncoder();
    const salt = encoder.encode('page-notes-salt-v1'); // Must match extension salt exactly
    const keyMaterial = await crypto.subtle.importKey(
        'raw',
        encoder.encode(userId),
        'PBKDF2',
        false,
        ['deriveBits', 'deriveKey']
    );

    return crypto.subtle.deriveKey(
        {
            name: 'PBKDF2',
            salt: salt,
            iterations: 100000,
            hash: 'SHA-256'
        },
        keyMaterial,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt', 'decrypt']
    );
}

/**
 * Encrypt note content
 * @param {string} content - Plain text content
 * @param {CryptoKey} key - Encryption key
 * @returns {Promise<Object>} { encryptedData, iv }
 */
export async function encryptNote(content, key) {
    const encoder = new TextEncoder();
    const iv = crypto.getRandomValues(new Uint8Array(12));

    const encrypted = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv },
        key,
        encoder.encode(content)
    );

    return {
        encryptedData: arrayBufferToBase64(encrypted),
        iv: arrayBufferToBase64(iv)
    };
}

/**
 * Decrypt note content
 * @param {Object} data - { encryptedData, iv }
 * @param {CryptoKey} key - Encryption key
 * @returns {Promise<string>} Decrypted content
 */
export async function decryptNote(data, key) {
    // Convert from base64 - must match extension exactly
    const encryptedData = Uint8Array.from(atob(data.encryptedData), c => c.charCodeAt(0));
    const iv = Uint8Array.from(atob(data.iv), c => c.charCodeAt(0));

    const decrypted = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: iv },
        key,
        encryptedData
    );

    const decoder = new TextDecoder();
    return decoder.decode(decrypted);
}

/**
 * Initialize encryption key for current user
 * @param {string} userId - Firebase user ID
 */
export async function initEncryption(userId) {
    encryptionKey = await deriveKey(userId);
    return encryptionKey;
}

/**
 * Get current encryption key
 * @returns {CryptoKey|null}
 */
export function getEncryptionKey() {
    return encryptionKey;
}

// Helper functions
function arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
}

function base64ToArrayBuffer(base64) {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
}
